/*  iceberg-ui  */
import { IconWithVariant } from '@auviknetworks/iceberg-ui';

/*  MUI  */
import { Search, SearchOff } from '@mui/icons-material';
import {
    Box,
    Button,
    FormLabel,
    Grid,
    Paper,
    TableCell,
    TableRow,
    Typography
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DatePicker } from '@mui/x-date-pickers';

/*  Shared  */
import { ApplicationNameLinkCell } from 'application/Shared/Components/ApplicationNameLinkCell';
import { DateFormat } from 'application/Shared/Components/Core';
import { ListUserAccounts } from 'application/Shared/Components/ListUserAccounts';
import {
    AccountPicker,
    AppPicker,
    RiskPicker,
    UserPicker
} from 'application/Shared/Components/Pickers';
import { ManagedTable } from 'application/Shared/Components/Table';
import { TooltipQ } from 'application/Shared/Components/Tooltips';
import { FontSizes } from 'application/Shared/Constants';
import {
    Notify,
    TableHeaders,
    URL,
    dateTimeString
} from 'application/Shared/Helpers';
import { Incident, UserReq } from 'application/Shared/Requests';
import { debug } from 'application/Shared/Utils';

/* NPM */
import { endOfDay, startOfDay, subDays } from 'date-fns';
import dayjs from 'dayjs';

/*  Objects  */
import {
    Activity,
    ActivityIconAndName,
    ActivityIdAndIPAddress
} from 'objects/Activity';
import { Application } from 'objects/Application';
import { User, UserIconAndName } from 'objects/User';

/*  React  */
import React, { Component, useMemo } from 'react';
import { useTranslation, withTranslation } from 'react-i18next';
import ReactJson from 'react-json-view';
import { withRouter } from 'router/withRouter';

/*  Constants  */
const resource = 'login';

const determinePickerSelection = (value) => {
    if (Array.isArray(value) && value.length === 1 && value[0] === '0') {
        return '0';
    }

    return value;
};

const RenderAccount = ({
    genericCred,
    personalCred,
    loginUsername,
    sharedCred
}) => {
    if (!loginUsername) {
        return '-';
    }

    const accounts = [
        {
            personal: personalCred,
            service: genericCred,
            shared: sharedCred,
            username: loginUsername
        }
    ];

    return <ListUserAccounts accounts={accounts} noIcon />;
};

const FlagIcon = ({ flag }) => {
    if (flag === 'None') {
        return null;
    }

    const iconStyle = {
        alignContent: 'center',
        display: 'flex',
        marginRight: '4px'
    };

    return (
        <>
            {flag === 'personalCred' && (
                <IconWithVariant
                    variant="primary"
                    src="ProfileWithoutCircle"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            )}
            {flag === 'genericCred' && (
                <IconWithVariant
                    variant="primary"
                    src="BuildWrench"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            )}
            {flag === 'sharedCred' && (
                <IconWithVariant
                    variant="primary"
                    src="Users"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            )}
            {/* Not really in the backend, but leaving it here for when we do  */}
            {flag === 'workCred' && (
                <IconWithVariant
                    variant="primary"
                    src="Domain"
                    size={FontSizes[2]}
                    wrapperProps={{ ...iconStyle }}
                />
            )}
        </>
    );
};

const searchFields = ['application.name', 'id', 'username'];

const LogsTable = ({ parameters, getHeaders, renderRow, toggles }) => {
    const { t } = useTranslation();

    const headers = useMemo(() => getHeaders(), [getHeaders]);

    const csvExportFields = useMemo(
        () => [
            { key: 'id', label: t('Common.id') },
            { key: 'applicationName', label: t('Common.app_name') },
            {
                key: 'applicationCategory',
                label: t('Common.applicationCategory')
            },
            {
                key: 'applicationSubcategory',
                label: t('Common.applicationSubcategory')
            },
            { key: 'applicationRisk', label: t('Logs.applicationRisk') },
            {
                key: 'tenantUserCount',
                label: t('Common.applicationUniqueUsers')
            },
            { key: 'firstName', label: t('Common.userFirstName') },
            { key: 'lastName', label: t('Common.userLastName') },
            { key: 'email', label: t('Common.userEmail') },
            { key: 'activity', label: t('Common.activity') },
            { key: 'loginUsername', label: t('Common.loginUsername') },
            {
                key: 'hasFlags',
                label: t('Logs.hasFlags'),
                transform: 'bool_YN'
            },
            { key: 'flagsText', label: t('Common.flags') },
            { key: 'metadata', label: t('Common.metadata') }
        ],
        [t]
    );

    return (
        <ManagedTable
            csvBuildOnRequest
            csvExport
            csvExportFields={csvExportFields}
            csvExportFileName="ApplicationLogs"
            headers={headers}
            manageRows
            maxExportRows={100_000}
            parameters={parameters}
            renderBody={renderRow}
            renderBodyKey="id"
            renderFooter
            rowsApi="/appLog"
            rowsPerPage={25}
            searchEnabled
            searchFields={searchFields}
            sortDirection="DESC"
            sortFieldType="date"
            sortKey="activityTimestamp"
            toggles={toggles}
        />
    );
};

const RenderFlags = ({ id, flags }) => {
    const { t } = useTranslation();

    if (!flags || !Array.isArray(flags)) {
        return null;
    }

    if (flags.length === 0) {
        return null;
    }

    return (
        <Box>
            {flags.map((flag) => (
                <Box
                    key={`log_${id}_flag_${flag}`}
                    display="flex"
                    width="100%"
                    alignItems="center"
                >
                    <FlagIcon flag={flag} />
                    {t(`Flags.${flag}`)}
                </Box>
            ))}
        </Box>
    );
};

const RenderMetadata = ({ id, opened, metadata }) => {
    const { t } = useTranslation();
    const theme = useTheme();

    if (!opened[id] || !metadata) {
        return null;
    }

    return (
        <TableRow>
            <TableCell
                sx={{ backgroundColor: 'metadata.background' }}
                colSpan={100}
            >
                <ReactJson
                    style={{
                        backgroundColor: theme.palette.metadata.background,
                        width: '100%'
                    }}
                    name={t('Logs.activityMetadata')}
                    theme="monokai"
                    collapseStringsAfterLength={140}
                    src={metadata}
                    enableClipboard={false}
                />
            </TableCell>
        </TableRow>
    );
};

const RenderUser = ({ platformUserId, userMap }) => {
    const user = userMap[platformUserId];
    if (!user) {
        return null;
    }

    return (
        <User data={user}>
            <UserIconAndName link />
        </User>
    );
};

const RenderRow = ({ opened, renderMetadataButtons, row, userMap }) => {
    const { t } = useTranslation();
    const { activity, application, flags, id, ip, metadata, timestamp } = row;
    const { risk } = application;

    return (
        <React.Fragment key={`log_${id}`}>
            <TableRow>
                <TableCell>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            whiteSpace: 'nowrap'
                        }}
                    >
                        <Typography variant="small" sx={{ fontWeight: 600 }}>
                            <DateFormat date={timestamp} />
                        </Typography>
                        <Activity activity={activity} id={id}>
                            <Typography variant="small">
                                <ActivityIdAndIPAddress ip={ip} />
                            </Typography>
                        </Activity>
                    </Box>
                </TableCell>
                <TableCell>
                    <Application
                        data={application}
                        fromPage={t('Common.logs')}
                        nameHasLink
                    >
                        <ApplicationNameLinkCell withCategory />
                    </Application>
                </TableCell>
                <TableCell>
                    {risk && <RiskPicker value={risk} visualOnly />}
                </TableCell>
                <TableCell>
                    <RenderUser
                        platformUserId={row.platformUserId}
                        userMap={userMap}
                    />
                </TableCell>
                <TableCell>
                    <RenderAccount
                        genericCred={row.genericCred}
                        personalCred={row.personalCred}
                        loginUsername={row.loginUsername}
                        sharedCred={row.sharedCred}
                    />
                </TableCell>
                <TableCell>
                    <Activity
                        activity={activity}
                        metadata={metadata}
                        renderLoginSubtext={false}
                    >
                        <ActivityIconAndName subText />
                    </Activity>
                </TableCell>
                <TableCell>
                    <RenderFlags flags={flags} id={id} />
                </TableCell>
                <TableCell>{renderMetadataButtons(row)}</TableCell>
            </TableRow>
            {opened[id] && metadata && (
                <RenderMetadata id={id} metadata={metadata} opened={opened} />
            )}
        </React.Fragment>
    );
};

class Logs extends Component {
    constructor(props) {
        super(props);
        this.state = {
            accountSelection: '0',
            appSelection: '0',
            defaultFlags: [],
            endDate: endOfDay(new Date()),
            flags: ['None'],
            loaded: false,
            opened: {},
            parameters: {},
            startDate: startOfDay(subDays(new Date(), 30)),
            toggles: [],
            userMap: {},
            userSelection: '0'
        };

        const clientId = props?.match?.params?.clientId;
        /*  requests  */
        this.Incident = new Incident();
        this.UserReq = new UserReq(clientId);
    }

    componentDidMount() {
        this.load();
    }

    componentWillUnmount() {
        this.unmount = true;
        this.unload();
    }

    load = async () => {
        const { subPage } = this.props;

        await this.loadUsers();
        await this.loadFlags();
        await this.handleURL();
        await this.handleParameters();

        if (!this.unmount) {
            this.setState({
                loaded: true,
                subPage: !!subPage
            });
        }
    };

    unload = () => {
        this.Incident.cancel();
        this.UserReq.cancel();
    };

    loadFlags = async () => {
        try {
            const flags = ['None'];

            let data;
            try {
                data = await this.Incident.getConfig({ resource });
                if (!data) {
                    return;
                }
            } catch (e) {
                return;
            }

            Object.values(data).forEach((config) => {
                const { flag } = config;
                if (flag) {
                    flags.push(flag);
                }
            });

            if (!this.unmount) {
                this.setState({ flags });
            }
        } catch (e) {
            debug.e('Error getting incident config for login logs:', e);
        }
    };

    loadUsers = async () => {
        let users;

        try {
            users = await this.UserReq.getAll();
            if (!users) {
                return;
            }
        } catch (e) {
            return;
        }

        const userMap = {};
        users.forEach((u) => {
            userMap[u.id] = u;
        });

        if (!this.unmount) {
            this.setState({ userMap });
        }
    };

    handleURL = async () => {
        if (this.unmount) {
            return;
        }

        const { location } = this.props;
        const params = URL.getParams(location);

        if (params.has('start') && params.has('end')) {
            const start = params.get('start');
            const end = params.get('end');
            this.setState({
                endDate: new Date(end),
                startDate: new Date(start)
            });
        }

        if (params.has('flag')) {
            this.setState({
                defaultFlags: [
                    params.get('flag').replace('serviceCred', 'genericCred')
                ]
            });
        }
    };

    handleParameters = async () => {
        if (this.unmount) {
            return;
        }

        const { t, days, applicationId, platformUserId } = this.props;
        const { accountSelection, appSelection, userSelection, endDate } =
            this.state;
        let { startDate } = this.state;

        if (days) {
            startDate = startOfDay(subDays(new Date(), parseInt(days, 10)));
        }

        if (startDate > endDate) {
            Notify.error(t('Errors.start_greater_than_end'));
            return;
        }

        const start = dateTimeString(startDate);
        const end = dateTimeString(endDate);

        // If dates are wrong, don't even make the api call
        if (start === null || end === null) return;

        const parameters = {
            activityTimestamp: `{"between": "${start}, ${end}"}`,
            deferUsers: true,
            includeFlags: true
        };
        const toggles = [];

        if (applicationId) {
            parameters.applicationId = `[${applicationId}]`;
        } else {
            toggles.push({
                defaultEnabled: true,
                key: 'excludePersonal',
                parameter: { field: 'excludePersonal', value: true },
                toggleText: `${t('Common.exclude_personal_applications')}`
            });
            if (
                appSelection !== '0' &&
                Array.isArray(appSelection) &&
                appSelection.length > 0
            ) {
                parameters.applicationId = `[${appSelection.join(',')}]`;
            }
        }

        if (platformUserId) {
            parameters.platformUserId = `[${platformUserId}]`;
        } else if (
            userSelection !== '0' &&
            Array.isArray(userSelection) &&
            userSelection.length > 0
        ) {
            parameters.platformUserId = `[${userSelection.join(',')}]`;
        }

        if (
            accountSelection !== '0' &&
            Array.isArray(accountSelection) &&
            accountSelection.length > 0
        ) {
            parameters.username = `[${accountSelection.join(',')}]`;
        }

        if (this.unmount) return;
        this.setState({ parameters, startDate, toggles });
    };

    getHeaders = () => {
        const { t, noSort } = this.props;
        const { flags, defaultFlags } = this.state;

        const flagFilters = flags.map((flag) => {
            const value = flag;
            let render = flag;

            if (flag !== 'None') {
                render = (
                    <Box
                        as="span"
                        display="flex"
                        alignItems="center"
                        key={`renderHeadersLogs_${flag}`}
                    >
                        <FlagIcon flag={flag} key={`icon_${flag}`} />
                        {t(`Flags.${flag}`)}
                        <TooltipQ
                            key={`tooltip_${flag}`}
                            text={t(`Tooltips.Logs.${flag}`)}
                        />
                    </Box>
                );
            }

            let checked = false;
            if (defaultFlags.length) {
                if (defaultFlags.includes(value)) {
                    checked = true;
                }
            }

            return {
                default: checked,
                key: value,
                parameter: 'hasFlags',
                render,
                value
            };
        });

        const table = new TableHeaders({ t });
        const headers = [
            [
                'Common.date',
                {
                    sortable: !noSort,
                    sortableFieldType: 'date',
                    sortableKey: 'timeUTC',
                    width: '10%'
                }
            ],
            [
                'Common.application',
                {
                    sortable: !noSort,
                    sortableFieldType: 'string',
                    sortableKey: 'applicationId',
                    width: '15%'
                }
            ],
            [
                'Common.risk',
                {
                    sortable: false,
                    width: '5%'
                }
            ],
            [
                'Common.user',
                {
                    sortable: !noSort,
                    sortableFieldType: 'string',
                    sortableKey: 'platformUserId',
                    width: '15%'
                }
            ],
            [
                'Common.account',
                {
                    sortable: false,
                    // TODO: Revisit the width percentages here.
                    width: '15%'
                }
            ],
            { component: 'ActivityType' },
            [
                'Common.incident_flags',
                {
                    field: 'hasFlags',
                    filterable: true,
                    filterableOptions: flagFilters,
                    name: 'hasFlags',
                    sortable: false,
                    squish: true,
                    tooltip: 'Logs.incident_flags',
                    width: '15%'
                }
            ],
            ['Common.metadata', { sortable: false, squish: true, width: '5%' }]
        ];

        return table.make(headers);
    };

    renderFilters() {
        const { t } = this.props;
        const { accountSelection, appSelection, userSelection } = this.state;

        return (
            <Grid container spacing={3}>
                <Grid item xs={4}>
                    <FormLabel>{t('Logs.application_filter')}:</FormLabel>
                    <AppPicker
                        multiple
                        allValue={t('Common.all_applications')}
                        allValueId="0"
                        value={appSelection}
                        allApplications
                        onChange={(value) => {
                            this.setState(
                                {
                                    appSelection:
                                        determinePickerSelection(value)
                                },
                                () => {
                                    this.handleParameters();
                                }
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormLabel>{t('Logs.user_filter')}:</FormLabel>
                    <UserPicker
                        multiple
                        allValue={t('Common.all_users')}
                        allValueId="0"
                        value={userSelection}
                        onChange={(value) => {
                            this.setState(
                                {
                                    userSelection:
                                        determinePickerSelection(value)
                                },
                                () => {
                                    this.handleParameters();
                                }
                            );
                        }}
                    />
                </Grid>
                <Grid item xs={4}>
                    <FormLabel>{t('Logs.accountFilter')}:</FormLabel>
                    <AccountPicker
                        multiple
                        allValue={t('Common.all_accounts')}
                        allValueId="0"
                        value={accountSelection}
                        onChange={(value) => {
                            this.setState(
                                {
                                    accountSelection:
                                        determinePickerSelection(value)
                                },
                                () => {
                                    this.handleParameters();
                                }
                            );
                        }}
                    />
                </Grid>
            </Grid>
        );
    }

    renderDateSearch() {
        const { t } = this.props;
        const { startDate, endDate } = this.state;

        return (
            <Grid container spacing={3} mt="4px">
                <Grid item xs={6} display="flex" alignItems="center">
                    <FormLabel style={{ marginRight: '8px' }}>
                        {t('Common.startDate')}:
                    </FormLabel>
                    <DatePicker
                        value={dayjs(startDate)}
                        onChange={(e) =>
                            this.setState(
                                { startDate: startOfDay(new Date(e)) },
                                () => {
                                    this.handleParameters();
                                }
                            )
                        }
                    />
                </Grid>
                <Grid item xs={6} display="flex" alignItems="center">
                    <FormLabel style={{ marginRight: '8px' }}>
                        {t('Common.end_date')}:
                    </FormLabel>
                    <DatePicker
                        value={dayjs(endDate)}
                        onChange={(e) =>
                            this.setState(
                                { endDate: endOfDay(new Date(e)) },
                                () => {
                                    this.handleParameters();
                                }
                            )
                        }
                    />
                </Grid>
            </Grid>
        );
    }

    renderMetadataButtons = (row) => {
        const { opened } = this.state;
        const { metadata, id } = row;

        if (!metadata) {
            return null;
        }

        if (opened[id]) {
            return (
                <Button
                    data-trackid="logs_hideMetadata"
                    size="small"
                    color="primary"
                    variant="contained"
                    onClick={() => {
                        this.setState({
                            opened: { ...opened, [row.id]: false }
                        });
                    }}
                >
                    <SearchOff />
                </Button>
            );
        }

        return (
            <Button
                size="small"
                color="primary"
                data-trackid="viewMetadata"
                variant="contained"
                onClick={() => {
                    this.setState({
                        opened: { ...opened, [row.id]: true }
                    });
                }}
            >
                <Search />
            </Button>
        );
    };

    render() {
        const { loaded, opened, parameters, subPage, toggles, userMap } =
            this.state;
        const { subPagePaper } = this.props;

        if (!loaded) {
            return null;
        }

        if (subPage) {
            return (
                <>
                    {!!subPagePaper && (
                        <Paper
                            elevation={0}
                            variant="outlined"
                            style={{ marginTop: '15px' }}
                            data-trackid="sharedPagesLogsPaperSubpageWithPaper"
                        >
                            {this.renderDateSearch()}
                            <LogsTable
                                parameters={parameters}
                                getHeaders={this.getHeaders}
                                renderRow={(row) => (
                                    <RenderRow
                                        opened={opened}
                                        renderMetadataButtons={
                                            this.renderMetadataButtons
                                        }
                                        row={row}
                                        userMap={userMap}
                                    />
                                )}
                                toggles={toggles}
                            />
                        </Paper>
                    )}
                    {!subPagePaper && (
                        <Box data-trackid="sharedPagesLogsPaperSubpageNoPaper">
                            {this.renderDateSearch()}
                            <LogsTable
                                parameters={parameters}
                                getHeaders={this.getHeaders}
                                renderRow={(row) => (
                                    <RenderRow
                                        opened={opened}
                                        renderMetadataButtons={
                                            this.renderMetadataButtons
                                        }
                                        row={row}
                                        userMap={userMap}
                                    />
                                )}
                                toggles={toggles}
                            />
                        </Box>
                    )}
                </>
            );
        }

        return (
            <Box width="100%">
                <Paper
                    elevation={0}
                    variant="outlined"
                    data-trackid="sharedPagesLogsPaperNoSubpage"
                >
                    {this.renderFilters()}
                    {this.renderDateSearch()}
                    <LogsTable
                        parameters={parameters}
                        getHeaders={this.getHeaders}
                        renderRow={(row) => (
                            <RenderRow
                                opened={opened}
                                renderMetadataButtons={
                                    this.renderMetadataButtons
                                }
                                row={row}
                                userMap={userMap}
                            />
                        )}
                        toggles={toggles}
                    />
                </Paper>
            </Box>
        );
    }
}
export default withRouter(withTranslation()(Logs));
